import { currencies, Currency } from '~/constants/currency';
import * as moment from 'moment';
import {
  BlackbeardSignatureConfirmation,
  BlackbeardSignatureConfirmationExtra,
} from '~/pages/Orders/Blackbeard/LabelPurchase/modules/types';
import { fromJSON } from '~/types/Parcel';
import { parseBusinessRules } from '~/utils/businessRules';
import Address, * as address from './Address';
import * as APIError from './APIError';
import BusinessRule, { JSONBusinessRule } from './BusinessRule';
import CustomsDeclaration, {
  fromJSON as customsDeclarationFromJSON,
  fromWebCustomsDeclaration,
  JSONCustomsDeclaration,
  requiresCustomsDeclaration as isCustomsRequired,
  WebCustomsDeclaration,
} from './CustomsDeclaration';
import { WebRate } from './Rate';
import CustomsItem, {
  fromJSON as customsItemFromJSON,
  fromWebCustomsItem,
  JSONCustomsItem,
} from './CustomsItem';
import LineItem, { WebShipmentLineItemsInner } from './LineItem';
import {
  batchEntriesFromJSON,
  BatchEntry,
  BatchEntryStatus,
  JSONBatchEntry,
  UserBatchInput,
} from './OrdersBatch';
import Parcel, { JSONParcel, WebParcel, fromWebParcel } from './Parcel';

type RecipientType = 'LICENSEE' | 'CONSUMER';

interface JSONInsuranceExtra {
  amount: Maybe<string>;
  currency: string;
  use_imported_amount: boolean;
  content: string;
}

interface JSONAlcoholExtra {
  contains_alcohol: boolean;
  recipient_type: Maybe<RecipientType>;
}

interface JSONDryIceExtra {
  contains_dry_ice: boolean;
  weight: string;
  weight_unit_id: number;
}

export interface JSONHazmatExtra {
  contains: boolean;
  contains_lithium_batteries: boolean;
  contains_biological_material: boolean;
}

interface JSONShipmentExtras {
  signature_confirmation?: Maybe<BlackbeardSignatureConfirmation>;
  is_residential?: boolean;
  is_return_requested?: boolean;
  insurance?: Maybe<JSONInsuranceExtra>;
  is_bypass_address_validation?: boolean;
  alcohol?: Maybe<JSONAlcoholExtra>;
  is_bypass_customs?: boolean;
  dry_ice?: Maybe<JSONDryIceExtra>;
  packing_slip_text: Maybe<string>;
  dangerous_goods?: Maybe<JSONHazmatExtra>;
}

interface JSONBatchEntryExtended extends JSONBatchEntry {
  shipment_object_id: Maybe<string>;
  transaction_object_id: Maybe<string>;
  use_imported_weight: Maybe<boolean>;
  shipment_date: Maybe<string>;
  parcel?: JSONParcel;
  extras?: JSONShipmentExtras;
  customs_declaration?: JSONCustomsDeclaration;
  customs_items?: JSONCustomsItem[];
  is_bypass_customs?: boolean;
  line_items?: JSONLineItemLite[];
  address_from?: address.JSONAddress;
  address_return?: address.JSONAddress;
  address_to?: address.JSONAddress;
  partial_fulfillment_rank: number;
  preferred_service_level_token?: string;
  preferred_account_object_id?: string;
  errors?: APIError.JSONAPIError[];
  rules?: JSONBusinessRule[];
}

export type WebExtrasAlcohol = {
  contains_alcohol?: boolean;
  recipient_type?: 'CONSUMER' | 'LICENSEE' | null;
};

export type WebExtrasDryIce = {
  contains_dry_ice?: boolean;
  weight?: number;
  weight_unit_id?: string;
};

export type WebExtrasInsurance = {
  amount: string;
  content?: string;
  currency?: string;
  use_imported_amount?: boolean;
};

export type WebExtrasDangerousGoods = {
  contains: boolean;
  contains_lithium_batteries: boolean;
  contains_biological_material: boolean;
};

export type WebExtras = {
  alcohol: WebExtrasAlcohol;
  dry_ice: WebExtrasDryIce;
  insurance: WebExtrasInsurance;
  dangerous_goods: WebExtrasDangerousGoods;
  is_bypass_address_validation: boolean;
  is_bypass_customs: boolean;
  is_return_requested: boolean;
  packing_slip_text: string;
  signature_confirmation: string;
};

export type WebShipment = {
  id: string;
  address_from: address.WebAddress;
  address_return: address.WebAddress;
  address_to: address.WebAddress;
  alternate_address_to: string;
  carrier_accounts?: object[];
  customs_declaration?: WebCustomsDeclaration;
  extras?: WebExtras;
  is_return: boolean;
  line_items: WebShipmentLineItemsInner[];
  object_created: Date;
  object_owner: string;
  object_updated: Date;
  parcels: WebParcel[];
  rates: WebRate[];
  shipment_date: string;
  preferred_account_object_id: string;
  preferred_service_level_token: string;
  status: string;
  transaction: string;
};

export interface InsuranceExtra {
  amount: string;
  currency: Currency;
  useImportedAmount: boolean;
}

export interface SignatureConfirmationExtra {
  hasSignatureConfirmation: boolean;
  signatureConfirmationType: Maybe<BlackbeardSignatureConfirmation>;
}

interface AlcoholExtra {
  containsAlcohol: boolean;
  recipientType: Maybe<RecipientType>;
}

export interface DryIceExtra {
  containsDryIce: boolean;
  weight: string;
  weightUnitId: number;
}

export interface HazmatExtra {
  isHazmat: boolean;
  isLithiumBatteries: boolean;
  isBiologicalMaterial: boolean;
}

interface ShipmentExtras {
  signatureConfirmation: Maybe<BlackbeardSignatureConfirmationExtra>;
  isResidential?: boolean;
  isReturnRequested?: boolean;
  insurance: Maybe<InsuranceExtra>;
  isBypassAddressValidation: boolean;
  alcohol: Maybe<AlcoholExtra>;
  isBypassCustoms: boolean;
  dryIce: Maybe<DryIceExtra>;
  packingSlipText: Maybe<string>;
  hazmat: Maybe<HazmatExtra>;
}

interface BatchEntryExtended extends BatchEntry {
  shipmentObjectId: Maybe<string>;
  transactionObjectId: Maybe<string>;
  useImportedWeight: boolean;
  shipmentDate: Maybe<moment.Moment>;
  parcel?: Maybe<Parcel>;
  extras?: ShipmentExtras;
  customsDeclaration?: CustomsDeclaration;
  customsItems?: CustomsItem[];
  customsDeclarationId?: Maybe<number>;
  lineItems: LineItemLite[];
  addressFrom: Maybe<Address>;
  addressReturn: Maybe<Address>;
  addressTo: Maybe<Address>;
  partialFulfillmentRank: number;
  preferredServiceLevelToken?: string;
  preferredAccountObjectID?: string;
  errors: APIError.APIError[];
  rules: BusinessRule[];
}

interface PartialFulfillmentItemContent {
  lineItem: string;
  quantity: number;
  shouldDisableSplit?: boolean;
}

type PartialFulfillmentContents = PartialFulfillmentItemContent[];

type JSONBatchEntriesResponse = {
  data: JSONBatchEntryExtended[];
};

interface JSONPartialFulfillmentItemContent {
  line_item: string;
  quantity: number;
}

type JSONPartialFulfillmentContents = JSONPartialFulfillmentItemContent[];

function lineItemToPartialFulfillmentItemContent({ quantity, objectId }: LineItem | LineItemLite) {
  return {
    quantity,
    lineItem: objectId,
  };
}

function partialFulfillmentItemContentToJSON({
  lineItem,
  quantity,
}: PartialFulfillmentItemContent): JSONPartialFulfillmentItemContent {
  return {
    quantity: Number(quantity),
    line_item: lineItem,
  };
}

type JSONLineItemLite = {
  object_id: string;
  quantity: number;
};

type LineItemLite = {
  objectId: string;
  quantity: number;
};

function lineItemLiteFromJSON({ object_id, quantity }: JSONLineItemLite): LineItemLite {
  return {
    quantity,
    objectId: object_id,
  };
}

export const fromWebShipment = (shipment: WebShipment, orderId: string): BatchEntryExtended => {
  const firstParcel: WebParcel | undefined =
    shipment.parcels.length > 0 ? shipment.parcels[0] : undefined;
  const hasSignatureConfirmation = (shipment.extras?.signature_confirmation || '').length > 0;
  const customsItems = shipment.customs_declaration?.items?.map(fromWebCustomsItem);
  const userInput: UserBatchInput = {
    shipmentDate: moment(shipment.shipment_date),
    parcel: {
      // a unique parcel type specific to userBatchInput
      length: firstParcel?.length || '',
      width: firstParcel?.width || '',
      height: firstParcel?.height || '',
      distanceUnit: firstParcel?.distance_unit || 'in',
      weight: firstParcel?.weight || '',
      weightUnit: firstParcel?.mass_unit || 'lb',
      useImportedOrderWeight: firstParcel?.use_item_weight || false,
      templateToken: firstParcel?.carrier_parcel_template?.token,
      userParcelTemplateId: firstParcel?.user_parcel_template?.object_id,
    },
    addressFrom: address.fromWebAddress(shipment.address_from),
    addressReturn: address.fromWebAddress(shipment.address_return),
    extra: {
      signatureConfirmation: hasSignatureConfirmation,
      isResidential: shipment.address_to.is_residential,
      isReturn: shipment.is_return,
      insurance: {
        amount: shipment.extras?.insurance.amount || '',
        currency: shipment.extras?.insurance.currency || 'USD',
        useImportedOrderValue: shipment.extras?.insurance.use_imported_amount || false,
        content: shipment.extras?.insurance.content || '',
      },
    },
  };

  const extras: ShipmentExtras = {
    isResidential: shipment.address_to.is_residential,
    isReturnRequested: shipment.extras?.is_return_requested,
    isBypassAddressValidation: shipment.extras?.is_bypass_address_validation || false,
    isBypassCustoms: shipment.extras?.is_bypass_customs || false,
    packingSlipText: shipment.extras?.packing_slip_text,
    signatureConfirmation: {
      hasSignatureConfirmation: hasSignatureConfirmation,
      signatureConfirmationType:
        shipment.extras && <BlackbeardSignatureConfirmation>shipment.extras.signature_confirmation,
    },
    insurance: {
      amount: shipment.extras?.insurance.amount || '',
      currency: shipment.extras?.insurance.currency
        ? currencies[shipment.extras.insurance.currency]
        : currencies.USD,
      useImportedAmount: shipment.extras?.insurance.use_imported_amount || false,
    },
    alcohol: {
      containsAlcohol: shipment.extras?.alcohol.contains_alcohol || false,
      recipientType: shipment.extras?.alcohol.recipient_type || 'CONSUMER',
    },
    dryIce: {
      containsDryIce: shipment.extras?.dry_ice.contains_dry_ice || false,
      weight: shipment.extras?.dry_ice.weight?.toString() || '',
      weightUnitId: Number(shipment.extras?.dry_ice.weight_unit_id),
    },

    //nulls
    hazmat: {
      isHazmat: false,
      isLithiumBatteries: false,
      isBiologicalMaterial: false,
    },
  };

  const lineItems = shipment.line_items.map((it) => ({
    objectId: it.object_id,
    quantity: it.quantity,
  }));

  return {
    objectCreated: moment(shipment.object_created),
    objectUpdated: moment(shipment.object_updated),
    objectStatus: <BatchEntryStatus>shipment.status,
    isReturn: shipment.is_return,
    shipmentObjectId: shipment.id,
    transactionObjectId: shipment.transaction,
    useImportedWeight: firstParcel?.use_item_weight || false,
    shipmentDate: moment(shipment.shipment_date),
    parcel: firstParcel && fromWebParcel(firstParcel),
    customsDeclaration:
      shipment.customs_declaration && fromWebCustomsDeclaration(shipment.customs_declaration),
    addressFrom: address.fromWebAddress(shipment.address_from),
    addressReturn: address.fromWebAddress(shipment.address_return),
    addressTo: address.fromWebAddress(shipment.address_to),
    preferredServiceLevelToken: shipment.preferred_service_level_token,
    preferredAccountObjectID: shipment.preferred_account_object_id,
    orderObjectId: orderId,
    extras,
    userInput,
    customsItems,
    lineItems,

    //nulls
    id: 0,
    customsDeclarationId: undefined,
    rules: [],
    errors: [],
    partialFulfillmentRank: 0,
  };
};

function getBatchEntryLineItemsTotalWeight(beLineItems: LineItemLite[], lineItems: LineItem[]) {
  return beLineItems
    ?.reduce((summedWeight, lineItemLite) => {
      const lineItem = lineItems.find((x) => x.objectId === lineItemLite.objectId);
      return lineItem
        ? summedWeight + parseFloat(lineItem.unitWeight) * lineItemLite.quantity
        : summedWeight;
    }, 0)
    .toFixed(2);
}

function getBatchEntryLineItemsTotalValue(beLineItems: LineItemLite[], lineItems: LineItem[]) {
  return beLineItems
    .reduce((summedTotal, lineItemLite) => {
      const lineItem = lineItems.find((x) => x.objectId === lineItemLite.objectId);
      return lineItem
        ? summedTotal + parseFloat(lineItem.unitAmount) * lineItemLite.quantity
        : summedTotal;
    }, 0)
    .toFixed(2);
}

export function getBatchEntryCustomItemsTotalWeight(batchEntry: Maybe<BatchEntryExtended>): number {
  if (!batchEntry || !batchEntry.customsItems) return 0;

  const totalWeight = batchEntry?.customsItems
    .reduce((summedWeight, item) => {
      return summedWeight + parseFloat(item.convertedNetWeight);
    }, 0)
    .toFixed(2);
  return parseFloat(totalWeight);
}

function extrasFromJSON(jsonShipmentExtras: JSONShipmentExtras): ShipmentExtras {
  return {
    signatureConfirmation: jsonShipmentExtras.signature_confirmation
      ? {
          hasSignatureConfirmation: !!jsonShipmentExtras.signature_confirmation,
          signatureConfirmationType:
            jsonShipmentExtras.signature_confirmation as BlackbeardSignatureConfirmation,
        }
      : null,
    isResidential: jsonShipmentExtras.is_residential,
    isReturnRequested: jsonShipmentExtras.is_return_requested || false,
    insurance: jsonShipmentExtras.insurance
      ? {
          amount: jsonShipmentExtras.insurance.amount ? jsonShipmentExtras.insurance.amount : '',
          currency: currencies[jsonShipmentExtras.insurance.currency] || currencies.USD,
          useImportedAmount: jsonShipmentExtras.insurance.use_imported_amount,
        }
      : null,
    isBypassAddressValidation: !!jsonShipmentExtras.is_bypass_address_validation,
    alcohol: jsonShipmentExtras.alcohol
      ? {
          containsAlcohol: jsonShipmentExtras.alcohol.contains_alcohol,
          recipientType: jsonShipmentExtras.alcohol.recipient_type,
        }
      : null,
    isBypassCustoms: !!jsonShipmentExtras.is_bypass_customs,
    dryIce: jsonShipmentExtras.dry_ice
      ? {
          containsDryIce: jsonShipmentExtras.dry_ice.contains_dry_ice,
          weight: jsonShipmentExtras.dry_ice.weight,
          weightUnitId: jsonShipmentExtras.dry_ice.weight_unit_id,
        }
      : null,
    packingSlipText: jsonShipmentExtras.packing_slip_text,
    hazmat: jsonShipmentExtras.dangerous_goods
      ? {
          isHazmat: !!jsonShipmentExtras.dangerous_goods.contains,
          isLithiumBatteries: !!jsonShipmentExtras.dangerous_goods.contains_lithium_batteries,
          isBiologicalMaterial: !!jsonShipmentExtras.dangerous_goods.contains_biological_material,
        }
      : null,
  };
}

function batchEntryExtendedFromJSON(jsonBatchEntry: JSONBatchEntryExtended): BatchEntryExtended {
  const batchEntry = batchEntriesFromJSON(jsonBatchEntry);

  return {
    ...batchEntry,
    parcel: jsonBatchEntry.parcel ? fromJSON(jsonBatchEntry.parcel) : null,
    extras: jsonBatchEntry.extras ? extrasFromJSON(jsonBatchEntry.extras) : undefined,
    customsDeclaration: jsonBatchEntry.customs_declaration
      ? customsDeclarationFromJSON(jsonBatchEntry.customs_declaration)
      : undefined,
    shipmentObjectId: jsonBatchEntry.shipment_object_id,
    transactionObjectId: jsonBatchEntry.transaction_object_id,
    useImportedWeight: !!jsonBatchEntry.use_imported_weight,
    shipmentDate: jsonBatchEntry.shipment_date ? moment(jsonBatchEntry.shipment_date) : null,
    customsItems: jsonBatchEntry.customs_items
      ? jsonBatchEntry.customs_items.map(customsItemFromJSON)
      : undefined,
    lineItems: jsonBatchEntry.line_items ? jsonBatchEntry.line_items.map(lineItemLiteFromJSON) : [],
    addressFrom: jsonBatchEntry.address_from ? address.fromJSON(jsonBatchEntry.address_from) : null,
    addressReturn: jsonBatchEntry.address_return
      ? address.fromJSON(jsonBatchEntry.address_return)
      : null,
    addressTo: jsonBatchEntry.address_to ? address.fromJSON(jsonBatchEntry.address_to) : null,
    partialFulfillmentRank: jsonBatchEntry.partial_fulfillment_rank,
    preferredServiceLevelToken: jsonBatchEntry.preferred_service_level_token,
    preferredAccountObjectID: jsonBatchEntry.preferred_account_object_id,
    errors: jsonBatchEntry?.errors?.map(APIError.fromJSON) || [],
    rules: parseBusinessRules(jsonBatchEntry?.rules || new Array<JSONBusinessRule>()).businessRules,
  };
}

function extrasToJSON(extras: Partial<ShipmentExtras>): JSONShipmentExtras {
  return {
    signature_confirmation: extras.signatureConfirmation?.hasSignatureConfirmation
      ? extras.signatureConfirmation.signatureConfirmationType
      : extras.signatureConfirmation === null
        ? null
        : undefined,
    is_residential: extras.isResidential,
    is_return_requested: extras.isReturnRequested,
    insurance: extras.insurance
      ? {
          amount: !extras.insurance.useImportedAmount ? extras.insurance.amount : undefined,
          currency: extras.insurance.currency.iso,
          use_imported_amount: extras.insurance.useImportedAmount,
          content: '',
        }
      : extras.insurance,
    is_bypass_address_validation: extras.isBypassAddressValidation,
    alcohol:
      extras.alcohol && extras.alcohol.containsAlcohol
        ? {
            contains_alcohol: extras.alcohol.containsAlcohol,
            recipient_type: extras.alcohol.recipientType,
          }
        : extras.alcohol === null
          ? null
          : undefined,
    is_bypass_customs: extras.isBypassCustoms,
    dry_ice: extras.dryIce
      ? {
          contains_dry_ice: extras.dryIce.containsDryIce,
          weight: extras.dryIce.weight,
          weight_unit_id: extras.dryIce.weightUnitId,
        }
      : extras.dryIce,
    packing_slip_text: extras.packingSlipText,
    dangerous_goods: extras.hazmat
      ? {
          contains: extras.hazmat.isHazmat,
          contains_lithium_batteries: extras.hazmat.isLithiumBatteries,
          contains_biological_material: extras.hazmat.isBiologicalMaterial,
        }
      : extras.hazmat,
  };
}

function isReadyForPurchase(batchEntry: BatchEntryExtended): boolean {
  return (
    !!batchEntry.shipmentObjectId && batchEntry.objectStatus === BatchEntryStatus.COMPLETE_SHIPMENT
  );
}

function isPartialFulfillmentRequest(batchEntry: BatchEntryExtended): boolean {
  return batchEntry.partialFulfillmentRank > 0;
}

export function requiresCustomsDeclaration(batchEntry: BatchEntryExtended): boolean {
  const { addressTo, addressFrom } = batchEntry;
  if (!addressTo || !addressFrom) return false;
  return isCustomsRequired(addressTo, addressFrom);
}

// Returns batch entries ids sorted in ascending rank order
export function getBatchEntriesIdsSortedByRank(
  batchEntryIds: number[],
  batchEntries: HashMap<number, BatchEntryExtended>,
): number[] {
  return batchEntryIds
    .map((beId) => batchEntries[beId])
    .filter((be) => !!be)
    .sort((a, b) => a.partialFulfillmentRank - b.partialFulfillmentRank)
    .map((be) => be.id);
}

export function requiresNewRates(batchEntry: BatchEntryExtended) {
  if (!batchEntry) {
    return true;
  }

  return (
    batchEntry.objectStatus === BatchEntryStatus.SHIPMENT_REQUEST_FAILED ||
    (batchEntry.objectStatus === BatchEntryStatus.PENDING &&
      batchEntry.shipmentObjectId === null &&
      !!batchEntry.parcel)
  );
}

export {
  BatchEntryExtended,
  JSONBatchEntryExtended,
  ShipmentExtras,
  RecipientType,
  batchEntryExtendedFromJSON,
  extrasToJSON,
  isReadyForPurchase,
  JSONPartialFulfillmentContents,
  JSONPartialFulfillmentItemContent,
  PartialFulfillmentItemContent,
  partialFulfillmentItemContentToJSON,
  PartialFulfillmentContents,
  getBatchEntryLineItemsTotalWeight,
  getBatchEntryLineItemsTotalValue,
  LineItemLite,
  JSONLineItemLite,
  lineItemToPartialFulfillmentItemContent,
  JSONBatchEntriesResponse,
  isPartialFulfillmentRequest,
};
